/** Small presentation helpers: labels, icons, colours for the UI layer. */
import type { Action, Goal, Move, Phase, Resource } from '@village/shared';

export const MOVE_LABEL: Record<Move, string> = {
  north: 'Step north',
  east: 'Step east',
  south: 'Step south',
  west: 'Step west',
  stay: 'Stay put',
  toward_home: 'Toward home',
  toward_tree: 'Toward a tree',
  toward_water: 'Toward water',
  toward_rock: 'Toward a rock',
  toward_food: 'Toward food',
  toward_unknown: 'Toward the unknown',
  toward_build_site: 'Toward the build',
};

export const ACTION_LABEL: Record<Action, string> = {
  none: 'Carry on',
  chop: 'Chop',
  mine: 'Mine',
  pick: 'Pick',
  dig: 'Dig',
  place_dirt: 'Place dirt',
  place_stone: 'Place stone',
  place_wood: 'Place wood',
  plant: 'Plant',
  eat: 'Eat',
  drink: 'Drink',
  sleep: 'Sleep',
  settle: 'Settle here',
  build_hut: 'Build a hut',
  build_fence: 'Build a fence',
  build_bridge: 'Build a bridge',
  build_campfire: 'Build a campfire',
  build_path: 'Build a path',
  continue_building: 'Keep building',
  abandon_building: 'Abandon build',
  drop_wood: 'Drop wood',
  drop_stone: 'Drop stone',
  drop_dirt: 'Drop dirt',
};

export const GOAL_LABEL: Record<Goal, string> = {
  gather_wood: 'Gather wood',
  gather_stone: 'Gather stone',
  find_food: 'Find food',
  build: 'Build',
  explore: 'Explore',
  rest: 'Rest',
  go_home: 'Go home',
};

/** Present tense, for "what they are doing right now". */
export const ACTION_DOING: Partial<Record<Action, string>> = {
  chop: 'Chopping',
  mine: 'Mining',
  dig: 'Digging',
  pick: 'Picking',
  sleep: 'Sleeping',
  eat: 'Eating',
  drink: 'Drinking',
  plant: 'Planting',
  continue_building: 'Building',
};

export const RESOURCE_LABEL: Record<Resource, string> = {
  wood: 'Wood',
  stone: 'Stone',
  dirt: 'Dirt',
  food: 'Food',
};

export const RESOURCE_COLOR: Record<Resource, string> = {
  wood: 'var(--peach)',
  stone: 'var(--sky)',
  dirt: 'var(--sage)',
  food: 'var(--mint)',
};

const NOUL_LABEL: Record<string, string> = {
  good_spot_to_build: 'Good spot to build',
  good_spot_to_settle: 'Good spot to settle',
  should_change_goal: 'Should change goal',
  curious: 'Curious',
};

/** The Noul set is open-ended; anything unmapped is humanised from snake_case. */
export function noulLabel(key: string): string {
  const known = NOUL_LABEL[key];
  if (known) return known;
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Known Nouls first, in the order they read best; anything new after them. */
const NOUL_ORDER = ['good_spot_to_build', 'good_spot_to_settle', 'should_change_goal', 'curious'];

export function noulKeys(nouls: Record<string, number> | undefined): string[] {
  const keys = Object.keys(nouls ?? {}).filter((k) => typeof nouls?.[k] === 'number');
  return keys.sort((a, b) => {
    const ia = NOUL_ORDER.indexOf(a);
    const ib = NOUL_ORDER.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return a.localeCompare(b);
  });
}

/** `heading` in words: "heading to a tree", "heading north". */
export function headingLabel(move: Move): string | null {
  switch (move) {
    case 'stay':
      return null;
    case 'toward_home':
      return 'heading home';
    case 'toward_tree':
      return 'heading to a tree';
    case 'toward_water':
      return 'heading to the water';
    case 'toward_rock':
      return 'heading to a rock';
    case 'toward_food':
      return 'heading to food';
    case 'toward_build_site':
      return 'heading to the build site';
    default:
      return `heading ${move}`;
  }
}

export const BLUEPRINT_LABEL: Record<string, string> = {
  hut: 'Hut',
  fence: 'Fence',
  bridge: 'Bridge',
  campfire: 'Campfire',
  path: 'Path',
};

export const PHASE_LABEL: Record<Phase, string> = { dawn: 'Dawn', day: 'Day', dusk: 'Dusk', night: 'Night' };

/** Inline SVG so the phase icon matches the type rather than an emoji font. */
export function phaseIcon(phase: Phase): string {
  const stroke = 'currentColor';
  const common = `fill="none" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"`;
  switch (phase) {
    case 'dawn':
      return `<svg viewBox="0 0 24 24" aria-hidden="true"><g ${common}><path d="M3 18h18"/><path d="M7.5 18a4.5 4.5 0 0 1 9 0"/><path d="M12 5.5V3"/><path d="M5.6 8.1 4.2 6.7"/><path d="M18.4 8.1l1.4-1.4"/></g></svg>`;
    case 'day':
      return `<svg viewBox="0 0 24 24" aria-hidden="true"><g ${common}><circle cx="12" cy="12" r="4"/><path d="M12 2.6v2M12 19.4v2M2.6 12h2M19.4 12h2M5.4 5.4l1.4 1.4M17.2 17.2l1.4 1.4M18.6 5.4l-1.4 1.4M6.8 17.2l-1.4 1.4"/></g></svg>`;
    case 'dusk':
      return `<svg viewBox="0 0 24 24" aria-hidden="true"><g ${common}><path d="M3 18h18"/><path d="M7.5 18a4.5 4.5 0 0 1 9 0"/><path d="M12 3v3.2"/><path d="M9.6 5.4 12 7.8l2.4-2.4"/></g></svg>`;
    case 'night':
      return `<svg viewBox="0 0 24 24" aria-hidden="true"><g ${common}><path d="M20 14.2A8 8 0 1 1 9.8 4a6.4 6.4 0 0 0 10.2 10.2Z"/><path d="M17 4.5v2M16 5.5h2"/></g></svg>`;
  }
}

export const pad2 = (n: number) => String(n).padStart(2, '0');

export const timeOf = (hour: number, minute: number) => `${pad2(hour)}:${pad2(minute)}`;

export const pct = (n: number) => `${Math.round(n * 100)}%`;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function confidenceLevel(c: number): 'high' | 'mid' | 'low' {
  return c >= 0.9 ? 'high' : c >= 0.5 ? 'mid' : 'low';
}
